import { ProductItemFragment } from '@/features/pos/components/ProductCatalog/fragments/product'
import { ConfigurableProductQuery } from '@/features/pos/components/ProductCatalog/queries/configurableProduct'
import { ProductId } from '@/features/pos/components/ProductCatalog/types'
import { ConfigurableProduct } from '@/features/pos/components/ProductConfigurationModal/types'
import {
    transformProductToConfigurable,
    transformSelectedCategoryProductToConfigurable,
} from '@/features/pos/components/ProductConfigurationModal/utils/transforms'
import { useHandleQueryErrorWithToast } from '@/features/pos/hooks/useHandleQueryErrorWithToast'
import { useFragment, useQuery } from '@apollo/client'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

export const useConfigurableProduct = (id: ProductId): ConfigurableProduct | undefined => {
    const { data: selectedCategoryProduct, complete: isSelectedCategoryProductComplete } =
        useFragment({
            fragment: ProductItemFragment,
            from: { __typename: 'Product', uuid: id },
        })

    const {
        data: configurableProduct,
        error,
        loading,
    } = useQuery(ConfigurableProductQuery, {
        variables: { id },
        skip: !id,
        fetchPolicy: 'cache-and-network',
    })

    useHandleQueryErrorWithToast(error, loading)

    const { t } = useTranslation()

    return useMemo(() => {
        if (configurableProduct?.product) {
            return transformProductToConfigurable(configurableProduct.product, t)
        }

        if (!isSelectedCategoryProductComplete) return undefined

        return transformSelectedCategoryProductToConfigurable(selectedCategoryProduct)
    }, [
        configurableProduct?.product,
        isSelectedCategoryProductComplete,
        selectedCategoryProduct,
        t,
    ])
}
